// client/src/api/cognitiveAPI.js

import axiosInstance from '../utilities/axiosInstance';

const cognitiveAPI = {
  // ============================================================================
  // TEXT-TO-SPEECH
  // ============================================================================

  /**
   * Convert text to speech using Azure TTS
   * @param {string} text - Text to convert to speech
   * @param {string} voice - Voice name (e.g., 'en-US-JennyNeural')
   * @param {string} language - Language code (e.g., 'en-US')
   * @returns {Promise<{success: boolean, audioData?: string, format?: string, error?: string}>}
   */
  textToSpeechAzure: async (text, voice = 'en-US-JennyNeural', language = 'en-US') => {
    try {
      const response = await axiosInstance.post('/api/cognitive/textToSpeechAzure', {
        text,
        voice,
        language
      });
      return response.data; // {success, audioData, format}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to convert text to speech(Azure): ${error.message}` 
      };
    }
  },

  /**
   * Convert text to speech using OpenAI TTS
   * @param {string} text - Text to convert to speech (max 4096 chars)
   * @param {string} voice - OpenAI voice: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'
   * @param {string} model - 'tts-1' (faster) | 'tts-1-hd' (higher quality)
   * @returns {Promise<{success: boolean, audioData?: string, format?: string, error?: string}>}
   */
  textToSpeech: async (text, voice = 'alloy', model = 'tts-1') => {
    try {
      const response = await axiosInstance.post('/api/cognitive/textToSpeech', {
        text,
        voice,
        model
      });
      return response.data; // {success, audioData, format}
    } catch (error) {
      return error?.response?.data || {
        success: false,
        error: `Failed to convert text to speech: ${error.message}`
      };
    }
  },

  // ============================================================================
  // SPEECH-TO-TEXT
  // ============================================================================

  /**
   * Convert speech to text using OpenAI Whisper
   * @param {Blob} audioBlob - Audio blob to transcribe
   * @param {string} language - Language code (e.g., 'en')
   * @returns {Promise<{success: boolean, text?: string, duration?: number, language?: string, error?: string}>}
   */
  speechToText: async (audioBlob, language = 'en') => {
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'speech.webm');
      formData.append('language', language);

      const response = await axiosInstance.post('/api/cognitive/speechToText', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data; // {success, text, duration, language}
    } catch (error) {
      return error?.response?.data || { 
        success: false, 
        error: `Failed to transcribe audio: ${error.message}` 
      };
    }
  }
};

export default cognitiveAPI;